import {
  saveReferralAzure,
  fetchReferralAzure,
  AzureTeamMember,
  fetchAzureReferralSolutions,
  AzureReferralSolution,
} from '../../../api/CoSell/index.ts'
import { postCrmFieldValues } from '../../../../common/api/mappingManager'
import { camelize, snakeize } from 'casing'
import {
  LoadingTypes,
  startLoading,
  stopLoading,
  emitSalesforceResourceSaved,
} from '@labrav/common'

import type { AppDispatch, RootState } from '../../../../store'
import type { ReferralMetadata } from '../reducer'
import { errorLogger } from '../../../../common/utils/errorLogger.ts'
import { AxiosError } from 'axios'
import { APIReferralError } from '../../../../externalApps/api/errorHandlers.ts'
import { removeEmptyValues } from '../../../../common/utils/CoSellHelpers/helperFunctions.ts'
import {
  trimReferralPostalCodeForApi,
  trimReferralStringField,
  getHubspotDealIdFromSearch,
} from '../../../../common/utils/CoSellHelpers/referralSubmitStringUtils'
import { isEmpty, omit } from 'lodash'
import { CRMTypes } from '../../../../common/utils/constants'
import { canvasAuthService } from '../../../../common/services/CanvasAuthService'
import { ReferralMetadataType } from '../types.ts'
import { ActionType } from '../../../../externalApps/modules/referrals/actions.ts'
import {
  ReferralAzureFormSections,
  ReferralLeadFormSectionName,
  CustomerDetails,
  DealDetails,
  SolutionsOffered,
  CompanySolution,
  MicrosoftSolution,
  HelpFromMicrosoft,
  PartialAzureFormObject,
  ReferralAzureForm,
  ApiSolutionFormat,
  AzureTypeOfHelp,
  ContactDetails,
} from './types.ts'
import {
  getDealTypeForSellerDealView,
  isAzureDealType,
  isMicrosoftHelpRequested,
  mapAzureCustomerPurchaseIntent,
  mapFormDealTypeToReferralType,
  normalizeAzureTypeOfHelp,
  mapAzureTypeOfHelpToApi,
  AZURE_MPC_REFERRAL_TYPE,
} from './utils'
import { isPrivateDealType } from './dealTypeUtils'
import type {
  AzureFormStatus,
  ReferralSalesStage,
  ReferralStatus,
  ReferralSubstatus,
} from './azureReferralSchema'
import {
  AZURE_FORM_STATUS,
  AZURE_REFERRAL_CREATE_STATUS,
  AZURE_REFERRAL_CREATE_SUBSTATUS,
  AZURE_REFERRAL_NEW_SALES_STAGE,
  AZURE_SOLUTION_API_TO_TYPE,
  AZURE_SOLUTION_TYPE,
  AZURE_SOLUTION_TYPE_TO_API,
  isAzureFormStatus,
  isAzureSolutionType,
  toReferralSalesStageString,
} from './azureReferralSchema'
import { isAdminRole } from '../../../../common/utils/isAdminRole.ts'
import {
  getHubspotCosellAccountIdFromPath,
  getHubspotOppsyncAccountIdFromState,
  getPartnerIdFromState,
  getTenantPartnerIdFromState,
  type PartnerTypeValue,
} from '../../../../common/utils/Hooks/partnerStateSelectors.ts'
import {
  SOLUTION_AREAS,
  SOLUTION_PLAYS,
  getSolutionPlaysForArea,
  normalizeAzureCountryToIso2,
  type SolutionAreaType,
} from '../../../../externalApps/HubspotApp/ReferralForm/AzureReferralForm/constants'

const resolveAzureSubmitPartnerId = (state: RootState): string => {
  const pathname =
    typeof window !== 'undefined' && window.location?.pathname
      ? window.location.pathname
      : ''
  const partnerType: PartnerTypeValue = isAdminRole(
    state.userProfile?.userProfile
  )
    ? 'admin'
    : 'user'

  return (
    getHubspotCosellAccountIdFromPath(pathname) ||
    getPartnerIdFromState(state, pathname, partnerType) ||
    getHubspotOppsyncAccountIdFromState(state) ||
    getTenantPartnerIdFromState(state, partnerType, pathname) ||
    state.customForm?.hubspotForm?.customData?.referralMetadata?.partner_id ||
    state.AccountData[partnerType]?.partnerData?.partnerId ||
    ''
  )
}

const isInSalesforceContext = (): boolean => {
  try {
    const partnerId = canvasAuthService.getPartnerId()
    if (!partnerId || isEmpty(partnerId)) {
      return false
    }
    return true
  } catch {
    return false
  }
}

/**
 * Same source as `useCosellMappingManagerMultiCloudMode`: partner account CRM type,
 * then CRM auth payload from `/crms/:id` (no canvas / URL heuristics).
 */
export const resolvePartnerCrmTypeForCoSell = (
  state: RootState
): string => {
  const fromPartner = state.AccountData.user.partnerData?.crmType
  const fromCrmAuth = state.CRMAuth?.crmFormData?.crm_type
  const crmType = fromPartner || fromCrmAuth
  if (crmType) {
    return String(crmType).toLowerCase()
  }
  return CRMTypes.hubspot
}

/** From CRM service `crm_schema.new_platform_sync` (Redux CRMAuth). */
const resolveNewPlatformSyncFromCrmService = (
  state: RootState
): boolean | undefined => {
  const newPlatformSync =
    state.CRMAuth?.crmFormData?.crm_schema?.new_platform_sync
  return newPlatformSync === true ? true : undefined
}

const resolveAzureMpnId = (state: RootState): string | undefined => {
  const azureAccount =
    state.accountForm?.oppsync?.selectedAccountDetails?.AZURE?.MPNID ||
    state.accountForm?.oppsync?.accountDetails?.AZURE?.MPNID

  return azureAccount || undefined
}

/**
 * Normalize closingDateTime for Microsoft Partner Center.
 * Emits exactly one timezone form: `...Z` (preferred for UTC) or `...±HH:mm`
 * (never `...+00:00Z`).
 */
const formatClosingDateTimeForApi = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined
  const trimmed = dateStr.trim()
  if (!trimmed) return undefined

  // Date-only → midnight UTC
  if (!trimmed.includes('T')) {
    return `${trimmed}T00:00:00Z`
  }

  let value = trimmed

  // Sanitize malformed dual-timezone strings (e.g. ...+00:00Z / ...-05:00Z)
  if (/[+-]\d{2}:\d{2}Z$/i.test(value)) {
    value = value.slice(0, -1)
  }

  // Prefer Z when the offset is UTC
  if (/[+-]00:00$/.test(value)) {
    return value.replace(/[+-]00:00$/, 'Z')
  }

  if (/Z$/i.test(value)) {
    return value.replace(/z$/i, 'Z')
  }

  if (/[+-]\d{2}:\d{2}$/.test(value)) {
    return value
  }

  return `${value}Z`
}

const mapContactsToTeam = (
  contacts?: Array<{
    firstName?: string
    lastName?: string
    phone?: string
    email?: string
  }>
) => {
  if (!contacts || !Array.isArray(contacts) || contacts.length === 0) {
    return undefined
  }

  const team = contacts
    .filter(contact => {
      return (
        (contact.firstName && contact.firstName.trim() !== '') ||
        (contact.lastName && contact.lastName.trim() !== '') ||
        (contact.email && contact.email.trim() !== '') ||
        (contact.phone && contact.phone.trim() !== '')
      )
    })
    .map(contact => ({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      phoneNumber: contact.phone || '',
      email: contact.email || '',
    }))

  return team.length > 0 ? team : undefined
}

const buildPartnerTeamForApi = (
  dealDetails?: Partial<DealDetails>,
  teamMemberCatalog?: AzureTeamMember[]
) => {
  const selectedEmails =
    dealDetails?.teamMembers?.filter((email): email is string =>
      Boolean(email?.trim())
    ) ?? []

  if (selectedEmails.length > 0) {
    const catalogByEmail = new Map<string, AzureTeamMember>()

    for (const member of teamMemberCatalog ?? []) {
      if (member.email) {
        catalogByEmail.set(member.email, member)
      }
    }

    for (const contact of dealDetails?.contactDetails ?? []) {
      if (contact.email && !catalogByEmail.has(contact.email)) {
        catalogByEmail.set(contact.email, {
          firstName: contact.firstName || '',
          lastName: contact.lastName || '',
          email: contact.email,
          phoneNumber: contact.phone || '',
        })
      }
    }

    const team = selectedEmails
      .map(email => {
        const member = catalogByEmail.get(email)
        return {
          firstName: member?.firstName || '',
          lastName: member?.lastName || '',
          phoneNumber: member?.phoneNumber || '',
          email: member?.email || email,
        }
      })
      .filter(
        contact =>
          contact.firstName.trim() !== '' ||
          contact.lastName.trim() !== '' ||
          contact.email.trim() !== '' ||
          contact.phoneNumber.trim() !== ''
      )

    return team.length > 0 ? team : undefined
  }

  return mapContactsToTeam(dealDetails?.contactDetails)
}

const buildCustomerProfileTeam = (
  customerDetails?: CustomerDetails,
  helpFromMicrosoft?: HelpFromMicrosoft
) => {
  const s = trimReferralStringField
  const firstName =
    s(customerDetails?.firstName) || s(helpFromMicrosoft?.firstName)
  const lastName =
    s(customerDetails?.lastName) || s(helpFromMicrosoft?.lastName)
  const email = s(customerDetails?.email) || s(helpFromMicrosoft?.email)
  const phone =
    customerDetails?.phone?.toString() || helpFromMicrosoft?.phone?.toString()
  const hasContact =
    firstName ||
    lastName ||
    email ||
    (phone?.replace(/\D/g, '').length ?? 0) > 3

  if (!hasContact) return undefined

  return [
    {
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      phoneNumber: phone || '',
    },
  ]
}

const isValidSolutionArea = (
  solutionArea?: string
): solutionArea is SolutionAreaType =>
  Boolean(
    solutionArea && (SOLUTION_AREAS as readonly string[]).includes(solutionArea)
  )

const isValidConversationForArea = ({
  solutionArea,
  conversation,
}: {
  solutionArea?: string
  conversation?: string
}): boolean => {
  if (!conversation) return false
  if (isValidSolutionArea(solutionArea)) {
    return getSolutionPlaysForArea(solutionArea).includes(conversation)
  }
  if (solutionArea) {
    // Area present but not in FY27 map — do not emit unpaired Conversation
    return false
  }
  return Object.values(SOLUTION_PLAYS).some(plays =>
    plays.includes(conversation)
  )
}

const buildAdditionalRequirementsAttributes = (
  dealDetails?: Partial<DealDetails>
) => {
  const attributes: Array<{ id: string; type: string }> = []

  const solutionArea = dealDetails?.solutionArea?.trim()
  const solutionPlay = dealDetails?.solutionPlay?.trim()
  const partnerRole = dealDetails?.partnerRole?.trim()

  if (isValidSolutionArea(solutionArea)) {
    attributes.push({ id: solutionArea, type: 'SolutionArea' })
  }
  if (
    isValidConversationForArea({
      solutionArea,
      conversation: solutionPlay,
    }) &&
    solutionPlay
  ) {
    // MPC expects type Conversation (SolutionPlay is deprecated)
    attributes.push({ id: solutionPlay, type: 'Conversation' })
  }
  if (partnerRole) {
    attributes.push({ id: partnerRole, type: 'PartnerRole' })
  }
  if (dealDetails?.customerPurchaseIntent) {
    const purchaseIntent = mapAzureCustomerPurchaseIntent(
      dealDetails.customerPurchaseIntent
    )
    if (purchaseIntent) {
      attributes.push({
        id: purchaseIntent,
        type: 'CustomerMarketplaceIntent',
      })
    }
  }

  return attributes.length > 0 ? { attributes } : undefined
}

export const buildAzureReferralApiRequestPayload = (apiPayload: {
  direction: string
  crmType: string
  formStatus?: AzureFormStatus
  referralId?: string
  referralDetails: AzureReferralDetailsPayload
  newPlatformSync?: boolean
}): Record<string, unknown> => {
  const isDraft = apiPayload.formStatus === 'draft'
  const { oppsyncFields, target, ...contractDetails } =
    apiPayload.referralDetails

  // Keep Labra-only fields (website, location, …) on both draft and submit — they
  // sync to CRM via oppsyncFields even when MPC contract fields live at the
  // referral_details root.
  const preservedOppsyncFields =
    oppsyncFields && Object.keys(oppsyncFields).length > 0
      ? { oppsyncFields }
      : {}

  const referralDetails: Record<string, unknown> = isDraft
    ? {
        ...apiPayload.referralDetails,
        engagementId: apiPayload.referralDetails.engagementId ?? '',
        ...(apiPayload.referralDetails.salesStage !== undefined
          ? {
              salesStage: toReferralSalesStageString(
                apiPayload.referralDetails.salesStage
              ),
            }
          : {}),
      }
    : {
        ...contractDetails,
        ...preservedOppsyncFields,
        engagementId: apiPayload.referralDetails.engagementId ?? '',
        // Draft→submit updates may omit create-time defaults; MPC requires these.
        status:
          apiPayload.referralDetails.status || AZURE_REFERRAL_CREATE_STATUS,
        substatus:
          apiPayload.referralDetails.substatus ||
          AZURE_REFERRAL_CREATE_SUBSTATUS,
        salesStage:
          toReferralSalesStageString(
            apiPayload.referralDetails.salesStage ??
              AZURE_REFERRAL_NEW_SALES_STAGE
          ) ?? AZURE_REFERRAL_NEW_SALES_STAGE.toString(),
        target: Array.isArray(target)
          ? target.filter(
              entry =>
                typeof entry === 'object' && entry !== null && 'id' in entry
            )
          : [],
      }

  return {
    direction: apiPayload.direction,
    crm_type: apiPayload.crmType,
    ...(apiPayload.newPlatformSync === true
      ? { new_platform_sync: true }
      : {}),
    ...(apiPayload.formStatus ? { form_status: apiPayload.formStatus } : {}),
    ...(apiPayload.referralId ? { referral_id: apiPayload.referralId } : {}),
    referral_details: referralDetails,
  }
}

export enum AzureFormActions {
  SET_AZURE_FORM = 'AZURE_SET_FORM',
  UPDATE_AZURE_FORM = 'AZURE_UPDATE_FORM',
  SET_AZURE = 'AZURE_SET',
  SET_FORM_AZURE_ERRORS = 'AZURE_SET_FORM_ERRORS',
  CLEAR_AZURE_FORM = 'AZURE_CLEAR_FORM',
  SET_AZURE_TEAM_MEMBERS = 'AZURE_SET_TEAM_MEMBERS',
  SET_AZURE_SOLUTIONS = 'AZURE_SET_SOLUTIONS',
  SET_AZURE_STATUS = 'AZURE_SET_STATUS',
  SET_AZURE_FORM_METADATA = 'AZURE_SET_FORM_METADATA',
}

export const setAzureCreationData = (
  onboardingFormData: Partial<ReferralAzureFormSections>
) => ({
  type: AzureFormActions.SET_AZURE_FORM,
  payload: onboardingFormData,
})

export const clearAzureForm = () => ({
  type: AzureFormActions.CLEAR_AZURE_FORM,
})

export type AzureChangeType = {
  sectionName: ReferralLeadFormSectionName
  data:
    | Partial<CustomerDetails>
    | Partial<DealDetails>
    | Partial<SolutionsOffered>
    | Partial<HelpFromMicrosoft>
}

export const setAzureDetails = (referral: PartialAzureFormObject) => ({
  type: AzureFormActions.SET_AZURE,
  payload: referral,
})

export const updateAzureCreationData = (data: AzureChangeType) => ({
  type: AzureFormActions.UPDATE_AZURE_FORM,
  payload: data,
})

export const emptyAzureFormFieldErrors = {} as Record<
  keyof ReferralAzureForm,
  string
>

export const setAzureFormFieldErrors = (
  errors: Record<keyof ReferralAzureForm, string>
) => ({
  type: AzureFormActions.SET_FORM_AZURE_ERRORS,
  payload: errors,
})

export {
  AZURE_REFERRAL_NEW_SALES_STAGE,
  AZURE_REFERRAL_CREATE_STATUS,
  AZURE_REFERRAL_CREATE_SUBSTATUS,
} from './azureReferralSchema'

export type { ApiSolutionFormat }

export type AzureReferralDetailsPayload = {
  engagementId?: string
  name?: string
  mpnId?: string
  customerProfile?: {
    name?: string
    address?: {
      city?: string
      state?: string
      country?: string
      postalcode?: string
      addressLine1?: string
      addressLine2?: string
      address?: string
    }
    team?: Array<{
      firstName?: string
      lastName?: string
      phoneNumber?: string
      email?: string
    }>
  }
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  salesStage?: ReferralSalesStage | number
  statusReason?: string
  dealType?: string
  details?: {
    dealValue?: number
    currency?: string
    closingDateTime?: string
    notes?: string
    requirements?: {
      solutions?: ApiSolutionFormat[]
      additionalRequirements?: {
        attributes?: Array<{ id: string; type: string }>
      }
    }
  }
  consent?: {
    consentToContact?: boolean
    consentToShareReferralWithMicrosoftSellers?: boolean
    consentToToShareInfoWithOthers?: boolean
  }
  externalReferenceId?: string
  type?: string
  team?: Array<{
    firstName?: string
    lastName?: string
    phoneNumber?: string
    email?: string
  }>
  target?: Array<{ type?: string; id?: string }>
  inviteContext?: {
    notes?: string
    assistanceRequestCode?: string
  }
  oppsyncFields?: AzureOppsyncFields
}

type AzureOppsyncFields = {
  website?: string
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  fullName?: string
  location?: string
  marketingCampaignID?: string
  solutionArea?: string
  solutionPlay?: string
  partnerRole?: string
  customerPurchaseIntent?: string
  partnerNotes?: string
}

const resolveAzureReferralObjects = (
  referralDetails: Record<string, unknown>
): Record<string, unknown> => {
  if (referralDetails?.objects && typeof referralDetails.objects === 'object') {
    return referralDetails.objects as Record<string, unknown>
  }

  return omit(referralDetails, [
    'oppsyncFields',
    'parentObjectKey',
    'engagementId',
  ])
}

const resolveAzureOppsyncFields = (
  referralDetails: Record<string, unknown>
): AzureOppsyncFields | undefined => {
  const nestedObjects = referralDetails?.objects as
    | Record<string, unknown>
    | undefined
  const oppsyncFields =
    referralDetails?.oppsyncFields ?? nestedObjects?.oppsyncFields

  return oppsyncFields && typeof oppsyncFields === 'object'
    ? (oppsyncFields as AzureOppsyncFields)
    : undefined
}
// Convert from display format (AzureReferralSolution) to API format
export const mapSolutionToApiFormat = (
  solution: AzureReferralSolution,
  additionalData?: { price?: number; currency?: string; quantity?: number }
): ApiSolutionFormat => {
  const solutionType =
    isAzureSolutionType(solution.solution_type) &&
    AZURE_SOLUTION_TYPE_TO_API[solution.solution_type]
      ? AZURE_SOLUTION_TYPE_TO_API[solution.solution_type]
      : solution.solution_type

  const apiSolution: ApiSolutionFormat = {
    id: solution.id,
    name: solution.solution_name,
    solutionType,
    publisherName: solution.publisher_name,
  }

  if (additionalData?.price !== undefined) {
    apiSolution.price = additionalData.price
  }
  if (additionalData?.currency) {
    apiSolution.currency = additionalData.currency
  }
  if (additionalData?.quantity !== undefined) {
    apiSolution.quantity = additionalData.quantity
  }

  return apiSolution
}

const mapApiSolutionToFormSolution = (
  apiSolution: ApiSolutionFormat
): CompanySolution | MicrosoftSolution | null => {
  const converted = mapSolutionFromApiFormat(apiSolution)
  if (!converted) {
    return null
  }

  const solutionId = apiSolution.id
  const base = {
    solutionId,
    uuid: solutionId,
    solutionName: apiSolution.name,
    publisherName: apiSolution.publisherName,
    incentiveProgram: '-',
  }

  if (converted.solution_type === AZURE_SOLUTION_TYPE.COMPANY_SOLUTION) {
    return {
      ...base,
      solutionType: AZURE_SOLUTION_TYPE.COMPANY_SOLUTION,
    }
  }

  if (converted.solution_type === AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION) {
    return {
      ...base,
      solutionType: AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION,
      quantity: apiSolution.quantity ?? 1,
      price: apiSolution.price ?? 0,
    }
  }

  return null
}

export const mapFormSolutionsOfferedToApiFormat = (
  solutionsOffered: Partial<SolutionsOffered> | undefined,
  currency?: string
): ApiSolutionFormat[] => {
  if (!solutionsOffered) {
    return []
  }

  const apiSolutions: ApiSolutionFormat[] = []

  solutionsOffered.companySolutions?.forEach(solution => {
    if (!solution.solutionId || !solution.solutionName) {
      return
    }

    apiSolutions.push({
      id: solution.solutionId,
      name: solution.solutionName,
      solutionType: 'Company',
      publisherName: solution.publisherName,
    })
  })

  solutionsOffered.microsoftSolutions?.forEach(solution => {
    if (!solution.solutionId || !solution.solutionName) {
      return
    }

    const apiSolution: ApiSolutionFormat = {
      id: solution.solutionId,
      name: solution.solutionName,
      solutionType: 'Microsoft',
      publisherName: solution.publisherName,
    }

    if (solution.price !== undefined) {
      apiSolution.price = solution.price
    }
    if (solution.quantity !== undefined) {
      apiSolution.quantity = solution.quantity
    }
    if (currency) {
      apiSolution.currency = currency
    }

    apiSolutions.push(apiSolution)
  })

  return apiSolutions
}

const mapApiSolutionsToFormSolutions = (
  apiSolutions: ApiSolutionFormat[]
): {
  companySolutions: CompanySolution[]
  microsoftSolutions: MicrosoftSolution[]
  selectedSolutions: string[]
} => {
  const companySolutions: CompanySolution[] = []
  const microsoftSolutions: MicrosoftSolution[] = []
  const selectedSolutions: string[] = []

  apiSolutions.forEach(apiSolution => {
    const formSolution = mapApiSolutionToFormSolution(apiSolution)
    if (!formSolution) {
      return
    }

    selectedSolutions.push(formSolution.solutionId)

    if (formSolution.solutionType === AZURE_SOLUTION_TYPE.COMPANY_SOLUTION) {
      companySolutions.push(formSolution)
      return
    }

    microsoftSolutions.push(formSolution)
  })

  return {
    companySolutions,
    microsoftSolutions,
    selectedSolutions,
  }
}

const attachSolutionsToRequirements = (
  details: Record<string, unknown>,
  solutions: ApiSolutionFormat[]
) => {
  if (solutions.length === 0) {
    return
  }

  if (!details.requirements) {
    details.requirements = {}
  }

  ;(details.requirements as Record<string, unknown>).solutions = solutions
}

const buildApiSolutionsForSubmit = (
  solutionsOffered: Partial<SolutionsOffered>,
  availableSolutions: AzureReferralSolution[],
  currency?: string
): ApiSolutionFormat[] => {
  const apiSolutions = mapFormSolutionsOfferedToApiFormat(
    solutionsOffered,
    currency
  )
  const mappedIds = new Set(apiSolutions.map(solution => solution.id))

  const addFromCatalog = (
    solutionId?: string,
    additionalData?: { price?: number; currency?: string; quantity?: number }
  ) => {
    if (!solutionId || mappedIds.has(solutionId)) {
      return
    }

    const solution = availableSolutions.find(
      availableSolution =>
        availableSolution.id === solutionId ||
        availableSolution.solution_id === solutionId
    )

    if (!solution) {
      return
    }

    apiSolutions.push(mapSolutionToApiFormat(solution, additionalData))
    mappedIds.add(solutionId)
  }

  solutionsOffered.companySolutions?.forEach(solution => {
    if (!solution.solutionName) {
      addFromCatalog(solution.solutionId)
    }
  })

  solutionsOffered.microsoftSolutions?.forEach(solution => {
    if (!solution.solutionName) {
      addFromCatalog(solution.solutionId, {
        price: solution.price,
        quantity: solution.quantity,
        currency: currency || 'USD',
      })
    }
  })

  if (apiSolutions.length === 0) {
    solutionsOffered.solutionsOffered?.forEach(solutionId => {
      addFromCatalog(solutionId)
    })
  }

  return apiSolutions
}

// Convert from API format to display format (AzureReferralSolution)
export const mapSolutionFromApiFormat = (
  apiSolution: ApiSolutionFormat
): AzureReferralSolution | null => {
  const mappedType = AZURE_SOLUTION_API_TO_TYPE[apiSolution.solutionType || '']
  if (!mappedType) {
    // If we can't determine the type, return null
    return null
  }

  return {
    id: apiSolution.id,
    solution_id: apiSolution.id, // Use id as solution_id
    solution_name: apiSolution.name,
    publisher_name: apiSolution.publisherName,
    solution_type: mappedType,
  }
}

export const mapAzureFormToApi = (
  referral: Partial<ReferralAzureFormSections>,
  options?: { teamMemberCatalog?: AzureTeamMember[] }
): {
  direction: string
  crmType: string
  referralDetails: AzureReferralDetailsPayload
} => {
  const { customerDetails, dealDetails, helpFromMicrosoft, solutionsOffered } =
    referral

  const s = trimReferralStringField
  const address: Record<string, unknown> = {}
  const city = s(customerDetails?.city)
  const state = s(customerDetails?.state)
  const normalizedCountry = normalizeAzureCountryToIso2(
    customerDetails?.country
  )
  const country = normalizedCountry
  const postalCode = trimReferralPostalCodeForApi(
    country,
    customerDetails?.postalCode
  )
  const addressLine1 = s(customerDetails?.addressLine1)
  const addressLine2 = s(customerDetails?.addressLine2)
  if (city !== undefined) address.city = city
  if (state !== undefined) address.state = state
  if (country !== undefined) address.country = country
  if (postalCode !== undefined) address.postalcode = postalCode
  if (addressLine1 !== undefined) {
    address.addressLine1 = addressLine1
    address.address = addressLine1
  }
  if (addressLine2 !== undefined) address.addressLine2 = addressLine2

  const customerProfile: Record<string, unknown> = {}
  const customerName = s(customerDetails?.customerName)
  if (customerName !== undefined) customerProfile.name = customerName
  if (Object.keys(address).length > 0) customerProfile.address = address
  const customerTeam = buildCustomerProfileTeam(
    customerDetails,
    helpFromMicrosoft
  )
  if (customerTeam) customerProfile.team = customerTeam

  const details: Record<string, unknown> = {}
  if (dealDetails?.estimatedValue !== undefined) {
    details.dealValue = dealDetails.estimatedValue
  }
  if (dealDetails?.currency) details.currency = dealDetails.currency
  const closingDateTime = formatClosingDateTimeForApi(
    dealDetails?.estimatedCloseDate
  )
  if (closingDateTime) details.closingDateTime = closingDateTime
  if (dealDetails?.partnerNotes) details.notes = dealDetails.partnerNotes

  const additionalRequirements =
    buildAdditionalRequirementsAttributes(dealDetails)
  if (additionalRequirements) {
    details.requirements = { additionalRequirements }
  }

  const apiSolutions = mapFormSolutionsOfferedToApiFormat(
    solutionsOffered,
    dealDetails?.currency
  )
  attachSolutionsToRequirements(details, apiSolutions)

  const objects: Record<string, unknown> = {}
  const dealName = s(dealDetails?.dealName)
  if (dealName !== undefined) objects.name = dealName
  if (Object.keys(customerProfile).length > 0) {
    objects.customerProfile = customerProfile
  }

  const formDealType = dealDetails?.dealType
  if (
    !isMicrosoftHelpRequested(helpFromMicrosoft?.typeOfHelp) &&
    helpFromMicrosoft?.sellerDealView
  ) {
    objects.dealType = getDealTypeForSellerDealView(
      helpFromMicrosoft.sellerDealView
    )
    objects.type = AZURE_MPC_REFERRAL_TYPE.INDEPENDENT
  } else if (isAzureDealType(formDealType)) {
    objects.dealType = formDealType
    objects.type = mapFormDealTypeToReferralType(formDealType)
  }

  // Always true unless deal type is Private (NO seller visibility → Private).
  const consent: Record<string, unknown> = {
    consentToContact: true,
    consentToShareReferralWithMicrosoftSellers: !isPrivateDealType(
      objects.dealType as string | undefined
    ),
    consentToToShareInfoWithOthers: true,
  }

  if (dealDetails?.crmId !== undefined && dealDetails.crmId !== '') {
    objects.externalReferenceId = String(dealDetails.crmId)
  }
  if (dealDetails?.status !== undefined) objects.status = dealDetails.status
  if (dealDetails?.substatus !== undefined) {
    objects.substatus = dealDetails.substatus
  }
  if (dealDetails?.salesStage !== undefined) {
    objects.salesStage = dealDetails.salesStage
  }
  if (dealDetails?.closedReason !== undefined) {
    objects.statusReason = dealDetails.closedReason
  }
  const partnerTeam = buildPartnerTeamForApi(
    dealDetails,
    options?.teamMemberCatalog
  )
  if (partnerTeam) objects.team = partnerTeam
  if (Object.keys(details).length > 0) objects.details = details
  objects.consent = consent

  const inviteContext: Record<string, string> = {}
  const notesToMicrosoft = s(helpFromMicrosoft?.notesToMicrosoft)
  if (notesToMicrosoft !== undefined) {
    inviteContext.notes = notesToMicrosoft
  }
  if (helpFromMicrosoft?.typeOfHelp) {
    inviteContext.assistanceRequestCode = mapAzureTypeOfHelpToApi(
      helpFromMicrosoft.typeOfHelp
    )
  }
  if (Object.keys(inviteContext).length > 0) {
    objects.inviteContext = inviteContext
  }

  const oppsyncFields: Record<string, unknown> = {}
  if (customerDetails?.website) oppsyncFields.website = customerDetails.website
  if (dealDetails?.location) oppsyncFields.location = dealDetails.location
  if (dealDetails?.marketingCampaignID) {
    oppsyncFields.marketingCampaignID = dealDetails.marketingCampaignID
  }
  if (dealDetails?.partnerRole)
    oppsyncFields.partnerRole = dealDetails.partnerRole

  return {
    direction: 'CRM_TO_MPC',
    crmType: 'hubspot',
    referralDetails: {
      ...objects,
      ...(Object.keys(oppsyncFields).length > 0 ? { oppsyncFields } : {}),
    },
  }
}

type AzureReferralApiObjects = {
  name?: string
  dealType?: string
  type?: string
  externalReferenceId?: string
  status?: string
  substatus?: string
  salesStage?: string | number
  statusReason?: string
  team?: Array<Record<string, unknown>>
  customerProfile?: {
    name?: string
    address?: Record<string, unknown>
    team?: Array<Record<string, unknown>>
  }
  details?: {
    dealValue?: number
    currency?: string
    closingDateTime?: string
    notes?: string
    requirements?: {
      additionalRequirements?: {
        attributes?: Array<{ id?: string; type?: string }>
      }
      solutions?: ApiSolutionFormat[]
    }
  }
  consent?: Record<string, unknown>
  inviteContext?: {
    notes?: string
    assistanceRequestCode?: string
  }
}

export const mapAzureApiToForm = (
  values: unknown
): Partial<ReferralAzureFormSections> => {
  const camelizedValues = camelize(values)

  if (camelizedValues?.referralDetails) {
    const { referralDetails } = camelizedValues
    const objects = resolveAzureReferralObjects(
      referralDetails
    ) as AzureReferralApiObjects
    const oppsyncFields = resolveAzureOppsyncFields(referralDetails)

    if (!objects || Object.keys(objects).length === 0) {
      return {}
    }

    const customerDetails: Partial<CustomerDetails> = {}
    if (objects.customerProfile?.name) {
      customerDetails.customerName = objects.customerProfile.name
    }
    if (objects.customerProfile?.address) {
      const addr = objects.customerProfile.address
      const city = addr.city as string | undefined
      if (city) customerDetails.city = city
      const state = addr.state as string | undefined
      if (state) customerDetails.state = state
      const country = addr.country as string | undefined
      if (country) {
        customerDetails.country =
          normalizeAzureCountryToIso2(country) ?? country
      }
      const postalCode =
        (addr.postalCode as string | undefined) ||
        (addr.postalcode as string | undefined)
      if (postalCode) customerDetails.postalCode = postalCode
      const addressLine1 =
        (addr.addressLine1 as string | undefined) ||
        (addr.address as string | undefined)
      if (addressLine1) customerDetails.addressLine1 = addressLine1
      const addressLine2 = addr.addressLine2 as string | undefined
      if (addressLine2) customerDetails.addressLine2 = addressLine2
    }
    if (
      objects.customerProfile?.team &&
      Array.isArray(objects.customerProfile.team) &&
      objects.customerProfile.team.length > 0
    ) {
      const customerContact = objects.customerProfile.team[0]
      const firstName = customerContact.firstName as string | undefined
      if (firstName) customerDetails.firstName = firstName
      const lastName = customerContact.lastName as string | undefined
      if (lastName) customerDetails.lastName = lastName
      const email = customerContact.email as string | undefined
      if (email) customerDetails.email = email
      const phoneNumber = customerContact.phoneNumber as string | undefined
      if (phoneNumber) customerDetails.phone = phoneNumber
    }
    if (oppsyncFields) {
      if (oppsyncFields.website) customerDetails.website = oppsyncFields.website
      if (oppsyncFields.email) customerDetails.email = oppsyncFields.email
      if (oppsyncFields.phone) customerDetails.phone = oppsyncFields.phone
      if (oppsyncFields.firstName)
        customerDetails.firstName = oppsyncFields.firstName
      if (oppsyncFields.lastName)
        customerDetails.lastName = oppsyncFields.lastName
      if (oppsyncFields.fullName)
        customerDetails.fullName = oppsyncFields.fullName
    }

    const dealDetails: Partial<DealDetails> & {
      contactDetails?: ContactDetails[]
    } = {}
    if (objects.name) dealDetails.dealName = objects.name
    if (objects.dealType && isAzureDealType(objects.dealType)) {
      dealDetails.dealType = objects.dealType
    } else if (
      objects.type &&
      !(Object.values(AZURE_MPC_REFERRAL_TYPE) as readonly string[]).includes(
        objects.type
      ) &&
      isAzureDealType(objects.type)
    ) {
      dealDetails.dealType = objects.type
    }
    if (objects.externalReferenceId)
      dealDetails.crmId = objects.externalReferenceId
    if (objects.status !== undefined) dealDetails.status = objects.status
    if (objects.substatus !== undefined) {
      dealDetails.substatus = objects.substatus
    }
    if (objects.salesStage !== undefined) {
      dealDetails.salesStage = String(objects.salesStage)
    }
    if (objects.statusReason) dealDetails.closedReason = objects.statusReason
    if (
      objects.team &&
      Array.isArray(objects.team) &&
      objects.team.length > 0
    ) {
      dealDetails.contactDetails = objects.team.map(
        (member: Record<string, unknown>, index: number): ContactDetails => ({
          id:
            (member.id as string | undefined) ||
            `${Math.floor(Math.random() * 10000)}-${index}`,
          firstName: (member.firstName as string | undefined) || '',
          lastName: (member.lastName as string | undefined) || '',
          phone:
            (member.phoneNumber as string | undefined) ||
            (member.phone as string | undefined) ||
            '',
          email: (member.email as string | undefined) || '',
        })
      )
      // Pre-select team members by setting their email addresses
      dealDetails.teamMembers = objects.team
        .map((member: Record<string, unknown>) => member.email as string)
        .filter((email: string) => !!email)
    }
    if (objects.details) {
      if (objects.details.dealValue !== undefined) {
        dealDetails.estimatedValue = objects.details.dealValue
      }
      if (objects.details.currency)
        dealDetails.currency = objects.details.currency
      if (objects.details.closingDateTime) {
        dealDetails.estimatedCloseDate = objects.details.closingDateTime
      }
      if (objects.details.notes) {
        dealDetails.partnerNotes = objects.details.notes
      }
      const attributes =
        objects.details.requirements?.additionalRequirements?.attributes
      if (Array.isArray(attributes)) {
        attributes.forEach((attribute: { id?: string; type?: string }) => {
          if (attribute.type === 'SolutionArea' && attribute.id) {
            dealDetails.solutionArea = attribute.id
          }
          if (
            (attribute.type === 'Conversation' ||
              attribute.type === 'SolutionPlay') &&
            attribute.id
          ) {
            // SolutionPlay is deprecated; map inbound as Conversation (solutionPlay)
            dealDetails.solutionPlay = attribute.id
          }
          if (attribute.type === 'PartnerRole' && attribute.id) {
            dealDetails.partnerRole = attribute.id
          }
          if (attribute.type === 'CustomerMarketplaceIntent' && attribute.id) {
            const mappedIntent = mapAzureCustomerPurchaseIntent(attribute.id)
            if (mappedIntent) {
              dealDetails.customerPurchaseIntent = mappedIntent
            }
          }
        })
      }
    }
    if (oppsyncFields) {
      if (oppsyncFields.location) dealDetails.location = oppsyncFields.location
      if (oppsyncFields.marketingCampaignID) {
        dealDetails.marketingCampaignID = oppsyncFields.marketingCampaignID
      }
      if (oppsyncFields.solutionArea)
        dealDetails.solutionArea = oppsyncFields.solutionArea
      if (oppsyncFields.solutionPlay)
        dealDetails.solutionPlay = oppsyncFields.solutionPlay
      if (oppsyncFields.partnerRole)
        dealDetails.partnerRole = oppsyncFields.partnerRole
      if (oppsyncFields.customerPurchaseIntent) {
        const mappedIntent = mapAzureCustomerPurchaseIntent(
          oppsyncFields.customerPurchaseIntent
        )
        if (mappedIntent) {
          dealDetails.customerPurchaseIntent = mappedIntent
        }
      }
      if (oppsyncFields.partnerNotes && !dealDetails.partnerNotes) {
        dealDetails.partnerNotes = oppsyncFields.partnerNotes
      }
    }

    const solutionsOffered: Partial<SolutionsOffered> & {
      solutionsOffered?: string[]
      apiSolutions?: ApiSolutionFormat[]
    } = {}

    // Solutions come only from details.requirements.solutions.
    // Do not treat objects.target as solutions — MPC target is typically
    // BusinessProfileLocation (organization id), not a marketplace solution.
    if (
      objects.details?.requirements &&
      objects.details.requirements.solutions &&
      Array.isArray(objects.details.requirements.solutions)
    ) {
      // Store API solution objects with price, quantity, currency
      solutionsOffered.apiSolutions = objects.details.requirements.solutions

      // Convert API format solutions and store as IDs
      const solutionIds = objects.details.requirements.solutions
        .map(apiSol => {
          const converted = mapSolutionFromApiFormat(apiSol)
          return converted?.id || converted?.solution_id || apiSol.id
        })
        .filter((id): id is string => !!id)

      if (solutionIds.length > 0) {
        solutionsOffered.solutionsOffered = solutionIds.filter(
          (id: string, index: number, self: string[]) =>
            self.indexOf(id) === index
        )
      }
    }

    const legacySolutionIds = [
      ...(solutionsOffered.solutionsOffered || []),
    ].filter((id, index, self) => self.indexOf(id) === index)

    const formSolutionsFromApi = solutionsOffered.apiSolutions
      ? mapApiSolutionsToFormSolutions(solutionsOffered.apiSolutions)
      : {
          companySolutions: [] as CompanySolution[],
          microsoftSolutions: [] as MicrosoftSolution[],
          selectedSolutions: [] as string[],
        }

    const selectedSolutions = [
      ...formSolutionsFromApi.selectedSolutions,
      ...legacySolutionIds,
    ].filter((id, index, self) => self.indexOf(id) === index)

    const normalizedSolutionsOffered: SolutionsOffered | undefined =
      Object.keys(solutionsOffered).length > 0
        ? {
            companySolutions: formSolutionsFromApi.companySolutions,
            microsoftSolutions: formSolutionsFromApi.microsoftSolutions,
            selectedSolutions,
            ...(legacySolutionIds.length > 0
              ? { solutionsOffered: legacySolutionIds }
              : {}),
            ...(solutionsOffered.apiSolutions
              ? { apiSolutions: solutionsOffered.apiSolutions }
              : {}),
          }
        : undefined

    const helpFromMicrosoft: Partial<HelpFromMicrosoft> = {}
    if (
      objects.consent?.consentToShareReferralWithMicrosoftSellers !== undefined
    ) {
      // Map boolean to 'YES' | 'NO'
      helpFromMicrosoft.sellerDealView = objects.consent
        .consentToShareReferralWithMicrosoftSellers
        ? 'YES'
        : 'NO'
    }
    if (objects.inviteContext?.assistanceRequestCode) {
      const mappedTypeOfHelp = normalizeAzureTypeOfHelp(
        objects.inviteContext.assistanceRequestCode
      )
      if (mappedTypeOfHelp) {
        helpFromMicrosoft.typeOfHelp = mappedTypeOfHelp as AzureTypeOfHelp
      }
    }
    if (objects.inviteContext?.notes) {
      helpFromMicrosoft.notesToMicrosoft = objects.inviteContext.notes
    }

    return {
      customerDetails:
        Object.keys(customerDetails).length > 0 ? customerDetails : undefined,
      dealDetails:
        Object.keys(dealDetails).length > 0 ? dealDetails : undefined,
      solutionsOffered: normalizedSolutionsOffered,
      helpFromMicrosoft:
        Object.keys(helpFromMicrosoft).length > 0
          ? helpFromMicrosoft
          : undefined,
    } as Partial<ReferralAzureFormSections>
  }

  return camelizedValues as Partial<ReferralAzureFormSections>
}

const AZURE_LABRA_KEY_PREFIX = 'Labra__'

const hasAzureMpcShape = (obj: Record<string, unknown>): boolean =>
  obj.customerProfile != null ||
  obj.objects != null ||
  obj.referral_details != null ||
  obj.referralDetails != null

export const isAzureLabraKeyedReferralObject = (
  obj: unknown
): obj is Record<string, unknown> => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return false
  }
  const record = obj as Record<string, unknown>
  const hasLabraKeys = Object.keys(record).some(key =>
    key.startsWith(AZURE_LABRA_KEY_PREFIX)
  )
  return hasLabraKeys && !hasAzureMpcShape(record)
}

const firstPresent = <T>(
  ...values: Array<T | null | undefined>
): T | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return undefined
}

const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value)
  }
  return undefined
}

const splitAzureLabraList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map(item => asNonEmptyString(item))
      .filter((item): item is string => Boolean(item))
  }
  if (typeof value === 'string' && value) {
    return value
      .split(';')
      .map(item => item.trim())
      .filter(Boolean)
  }
  return []
}

const mapAzureSellerDealView = (
  value: unknown
): HelpFromMicrosoft['sellerDealView'] => {
  if (value === true) return 'YES'
  if (value === false) return 'NO'
  const normalized = asNonEmptyString(value)?.trim().toUpperCase()
  if (normalized === 'YES') return 'YES'
  if (normalized === 'NO') return 'NO'
  return undefined
}

const parseAzureEstimatedValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

/**
 * Maps a flat Labra-keyed CRM referral_object (HubSpot mapping-service shape)
 * into Azure Formik sections. Primary keys come from
 * azure_hubspot_referral_mapping_template; stale webapp keys are aliases only.
 * Partner team members (Primary Sales Rep) are not mapped — create-flow users
 * add them manually.
 */
export const azureLabraObjectToForm = (
  values: Record<string, unknown>
): Partial<ReferralAzureFormSections> => {
  const customerDetails: Partial<CustomerDetails> = {}
  const customerName = asNonEmptyString(values.Labra__Customer_Name__c)
  const website = asNonEmptyString(values.Labra__Website__c)
  const country = asNonEmptyString(values.Labra__Country__c)
  const state = asNonEmptyString(values.Labra__State__c)
  const city = asNonEmptyString(values.Labra__City__c)
  const addressLine1 = asNonEmptyString(
    firstPresent(
      values.Labra__Customer_Address_Line_1__c,
      values.Labra__Address__c
    )
  )
  const addressLine2 = asNonEmptyString(
    firstPresent(
      values.Labra__Customer_Address_Line_2__c,
      values.Labra__Address_Line_2__c
    )
  )
  const postalCode = asNonEmptyString(values.Labra__Postal_Code__c)
  const firstName = asNonEmptyString(
    firstPresent(
      values.Labra__Customer_First_Name__c,
      values.Labra__First_Name__c
    )
  )
  const lastName = asNonEmptyString(
    firstPresent(
      values.Labra__Customer_Last_Name__c,
      values.Labra__Last_Name__c
    )
  )
  const email = asNonEmptyString(
    firstPresent(values.Labra__Customer_Email__c, values.Labra__Email__c)
  )
  const phone = firstPresent(
    values.Labra__Customer_Phone__c,
    values.Labra__Phone__c
  )

  if (customerName) customerDetails.customerName = customerName
  if (website) customerDetails.website = website
  if (country) {
    customerDetails.country = normalizeAzureCountryToIso2(country) ?? country
  }
  if (state) customerDetails.state = state
  if (city) customerDetails.city = city
  if (addressLine1) customerDetails.addressLine1 = addressLine1
  if (addressLine2) customerDetails.addressLine2 = addressLine2
  if (postalCode) customerDetails.postalCode = postalCode
  if (firstName) customerDetails.firstName = firstName
  if (lastName) customerDetails.lastName = lastName
  if (email) customerDetails.email = email
  if (phone !== undefined && phone !== null && phone !== '') {
    customerDetails.phone = phone as CustomerDetails['phone']
  }

  const dealDetails: Partial<DealDetails> = {}
  const dealName = asNonEmptyString(values.Labra__Deal_Name__c)
  const partnerNotes = asNonEmptyString(
    firstPresent(values.Labra__Description__c, values.Labra__Partner_Notes__c)
  )
  const estimatedCloseDate = asNonEmptyString(
    values.Labra__Estimated_Close_Date__c
  )
  const estimatedValue = parseAzureEstimatedValue(
    firstPresent(
      values.Labra__Estimated_Deal_Value__c,
      values.Labra__Estimated_Value__c
    )
  )
  const currency = asNonEmptyString(
    firstPresent(
      values.Labra__customerSoftwareValueCurrency__c,
      values.Labra__Currency__c
    )
  )
  const location = asNonEmptyString(values.Labra__Location__c)
  const solutionArea = asNonEmptyString(
    firstPresent(
      values.Labra__MSFT_Solution_Area__c,
      values.Labra__Solution_Area__c
    )
  )
  const solutionPlay = asNonEmptyString(
    firstPresent(
      values.Labra__MSFT_Solution_Play__c,
      values.Labra__Solution_Play__c
    )
  )
  const partnerRole = asNonEmptyString(values.Labra__MSFT_Partner_Role__c)
  const customerPurchaseIntent = mapAzureCustomerPurchaseIntent(
    firstPresent(
      values.Labra__MSFT_Marketplace_purchase_intent__c,
      values.Labra__Customer_Purchase_Intent__c
    )
  )
  const marketingCampaignID = asNonEmptyString(
    values.Labra__Marketing_Campaign_ID__c
  )

  if (dealName) dealDetails.dealName = dealName
  if (partnerNotes) dealDetails.partnerNotes = partnerNotes
  if (estimatedCloseDate) dealDetails.estimatedCloseDate = estimatedCloseDate
  if (estimatedValue !== undefined) dealDetails.estimatedValue = estimatedValue
  if (currency) dealDetails.currency = currency
  if (location) dealDetails.location = location
  if (solutionArea) dealDetails.solutionArea = solutionArea
  if (solutionPlay) dealDetails.solutionPlay = solutionPlay
  if (partnerRole) dealDetails.partnerRole = partnerRole
  if (customerPurchaseIntent) {
    dealDetails.customerPurchaseIntent = customerPurchaseIntent
  }
  if (marketingCampaignID) {
    dealDetails.marketingCampaignID = marketingCampaignID
  }

  // Team members are not prefilled from CRM template field values (Primary Sales
  // Rep / partner team). Users add them manually on the create form.

  const solutionIds = splitAzureLabraList(
    firstPresent(
      values.Labra__Labra_Product__c,
      values.Labra__Solutions_Offered__c
    )
  )
  const solutionsOffered: SolutionsOffered | undefined =
    solutionIds.length > 0
      ? {
          companySolutions: [],
          microsoftSolutions: [],
          selectedSolutions: solutionIds,
          solutionsOffered: solutionIds,
        }
      : undefined

  const helpFromMicrosoft: Partial<HelpFromMicrosoft> = {}
  const typeOfHelp = normalizeAzureTypeOfHelp(
    firstPresent(
      values.Labra__How_can_Microsoft_help__c,
      values.Labra__Type_of_Help__c
    )
  )
  const notesToMicrosoft = asNonEmptyString(values.Labra__Notes_to_Microsoft__c)
  const sellerDealView = mapAzureSellerDealView(
    values.Labra__Microsoft_Partner_Center_Referral_Visibi__c
  )
  if (typeOfHelp) {
    helpFromMicrosoft.typeOfHelp = typeOfHelp as AzureTypeOfHelp
  }
  if (notesToMicrosoft) helpFromMicrosoft.notesToMicrosoft = notesToMicrosoft
  if (sellerDealView) helpFromMicrosoft.sellerDealView = sellerDealView

  return {
    ...(Object.keys(customerDetails).length > 0 ? { customerDetails } : {}),
    ...(Object.keys(dealDetails).length > 0 ? { dealDetails } : {}),
    ...(solutionsOffered ? { solutionsOffered } : {}),
    ...(Object.keys(helpFromMicrosoft).length > 0 ? { helpFromMicrosoft } : {}),
  }
}

export const submitAzureForm =
  (
    referral: Partial<ReferralAzureFormSections>,
    status: ReferralMetadata['referralStatus'] = 'submitted',
    referralId?: string,
    actionType?: ActionType,
    hubspotDealIdFromUrl?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const partnerData = getState().AccountData.user.partnerData
    const referralFromBackend =
      getState().leadReferralForm.referral?.referralObject
    const resolvedReferralId =
      referralId ||
      getState().azureReferralForm?.referral?.referralMetadata?.referralId ||
      getState().customForm?.hubspotForm?.customData?.referralMetadata
        ?.referral_id ||
      getState().leadReferralForm.referral?.referralMetadata?.referralId
    const isDraft = status === 'draft'
    const { leadStatusReason } =
      getState().leadReferralForm.referralForm.leadDetails

    const referralData: Partial<ReferralAzureFormSections> = referral

    const referralFromForm = removeEmptyValues(referralData)
    const referralPayload: Partial<ReferralAzureFormSections> = {
      ...(referralFromBackend ? camelize(referralFromBackend) : {}),
      ...Object.fromEntries(
        Object.entries(referralFromForm).filter(
          ([, value]) => value !== '' && value !== undefined
        )
      ),
    }

    if (leadStatusReason && referralPayload.dealDetails) {
      const mappedIntent = mapAzureCustomerPurchaseIntent(leadStatusReason)
      if (mappedIntent) {
        referralPayload.dealDetails.customerPurchaseIntent = mappedIntent
      }
    }

    if (isInSalesforceContext()) {
      const opportunityRecordId =
        canvasAuthService.getRecord()?.recordId?.trim() || undefined
      if (opportunityRecordId) {
        referralPayload.dealDetails = {
          ...referralPayload.dealDetails,
          crmId: opportunityRecordId,
        }
      }
    } else {
      const resolvedHubspotDealIdFromUrl =
        hubspotDealIdFromUrl ||
        (typeof window !== 'undefined' && window.location?.search
          ? getHubspotDealIdFromSearch(window.location.search)
          : undefined)

      let externalReferenceId: string | undefined = resolvedHubspotDealIdFromUrl

      if (!externalReferenceId && referralPayload.dealDetails?.crmId) {
        externalReferenceId = String(referralPayload.dealDetails.crmId)
      }

      if (externalReferenceId && referralPayload.dealDetails) {
        referralPayload.dealDetails.crmId = externalReferenceId
      }
    }
    const teamMemberCatalog = getState().azureReferralForm?.teamMembers || []
    const apiPayload = mapAzureFormToApi(referralPayload, { teamMemberCatalog })
    const isCreate = !resolvedReferralId

    const storedEngagementId = getState().azureReferralForm?.engagementId

    if (isCreate) {
      if (!isDraft) {
        apiPayload.referralDetails.status = AZURE_REFERRAL_CREATE_STATUS
        apiPayload.referralDetails.substatus = AZURE_REFERRAL_CREATE_SUBSTATUS
        apiPayload.referralDetails.salesStage = AZURE_REFERRAL_NEW_SALES_STAGE
        apiPayload.referralDetails.engagementId = ''
      }
    } else if (storedEngagementId !== undefined) {
      apiPayload.referralDetails.engagementId = storedEngagementId
    }

    // Convert solutions to API format and add to details.requirements.solutions
    const solutionsOffered = referralPayload.solutionsOffered
    if (solutionsOffered) {
      const availableSolutions = getState().azureReferralForm?.solutions || []
      const apiSolutions = buildApiSolutionsForSubmit(
        solutionsOffered,
        availableSolutions,
        referralPayload.dealDetails?.currency
      )

      if (apiSolutions.length > 0) {
        if (!apiPayload.referralDetails.details) {
          apiPayload.referralDetails.details = {}
        }
        const details = apiPayload.referralDetails.details as Record<
          string,
          unknown
        >
        attachSolutionsToRequirements(details, apiSolutions)
      }
    }

    const state = getState()
    const mpnId = resolveAzureMpnId(state)
    if (mpnId) {
      apiPayload.referralDetails.mpnId = mpnId
    }

    const crmTypeForApi = isInSalesforceContext()
      ? CRMTypes.salesforce
      : CRMTypes.hubspot
    const newPlatformSync = resolveNewPlatformSyncFromCrmService(state)

    const referralPayloadForApi = buildAzureReferralApiRequestPayload({
      ...apiPayload,
      crmType: crmTypeForApi,
      formStatus: isDraft ? 'draft' : 'submitted',
      ...(resolvedReferralId ? { referralId: resolvedReferralId } : {}),
      ...(newPlatformSync === true || isInSalesforceContext()
        ? { newPlatformSync: true }
        : {}),
    })

    const partnerId = resolveAzureSubmitPartnerId(state)

    const crmId = partnerData?.crmId || ''

    try {
      const { data } = await saveReferralAzure(referralPayloadForApi, partnerId)

      const camelizedData = camelize(data) as Record<string, unknown>
      const newReferralId =
        (camelizedData.referralId as string | undefined) || resolvedReferralId

      if (isCreate && (!newReferralId || isEmpty(newReferralId))) {
        throw new Error(
          'Failed to save referral: Missing referral ID in response'
        )
      }

      const lastReferralId = resolvedReferralId
        ? resolvedReferralId
        : newReferralId

      const responseFormData = mapAzureApiToForm(camelizedData)

      const newReferralObject: Partial<ReferralAzureFormSections> = {
        customerDetails: {
          ...referralPayload.customerDetails,
          ...responseFormData.customerDetails,
        },
        dealDetails: {
          ...referralPayload.dealDetails,
          ...responseFormData.dealDetails,
        },
        solutionsOffered: {
          companySolutions: [],
          microsoftSolutions: [],
          selectedSolutions: [],
          ...referralPayload.solutionsOffered,
          ...responseFormData.solutionsOffered,
        },
        helpFromMicrosoft: {
          ...referralPayload.helpFromMicrosoft,
          ...responseFormData.helpFromMicrosoft,
        },
      }

      const apiPayloadForStorage = mapAzureFormToApi(newReferralObject, {
        teamMemberCatalog: getState().azureReferralForm?.teamMembers || [],
      })

      await dispatch(setAzureCreationData(newReferralObject))

      const submittedFormStatus: AzureFormStatus = isDraft
        ? 'draft'
        : 'submitted'

      await dispatch(
        setAzureDetails({
          referralMetadata: {
            crmId: crmId,
            referralType: 'referral_opportunity' as ReferralMetadataType,
            referralStatus: status,
            crmParentTable: 'labra_referral',
            crmPrimaryKeyName: 'id',
            ...(!isEmpty(lastReferralId) && { referralId: newReferralId }),
            ...(!isEmpty(lastReferralId) && {
              crmPrimaryKeyValue: newReferralId,
            }),
          },
          referralObject: snakeize(apiPayloadForStorage) as Record<
            string,
            unknown
          >,
          referralObjectTransformed: newReferralObject,
        })
      )

      await dispatch(
        setAzureFormMetadata({
          formStatus: submittedFormStatus,
          ...(apiPayload.referralDetails.engagementId !== undefined
            ? { engagementId: apiPayload.referralDetails.engagementId }
            : {}),
        })
      )

      // Emits an event to Action Hub to refetch referrals.
      if (isInSalesforceContext()) {
        const brokerPartnerId =
          partnerId?.trim() ||
          canvasAuthService.getPartnerId()?.trim() ||
          partnerData?.partnerId?.trim() ||
          ''
        const parentOpportunityId =
          canvasAuthService.getRecord()?.recordId?.trim() || ''

        if (brokerPartnerId) {
          emitSalesforceResourceSaved({
            resourceType: 'referral',
            partnerId: brokerPartnerId,
            opportunityId: parentOpportunityId || null,
            referral: {
              id: newReferralId,
              draft: submittedFormStatus === 'draft',
            },
          })
        }
      }

      return {
        referralId: newReferralId,
        formData: newReferralObject,
        data: camelizedData,
      }
    } catch (e: unknown) {
      const error = e as AxiosError<APIReferralError>

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return { error: e }
    }
  }

export const setAzureTeamMembers = (teamMembers: AzureTeamMember[]) => ({
  type: AzureFormActions.SET_AZURE_TEAM_MEMBERS,
  payload: teamMembers,
})

export const setAzureSolutions = (solutions: AzureReferralSolution[]) => ({
  type: AzureFormActions.SET_AZURE_SOLUTIONS,
  payload: solutions,
})

export const setAzureStatus = (
  status: ReferralStatus | string,
  substatus: ReferralSubstatus | string
) => ({
  type: AzureFormActions.SET_AZURE_STATUS,
  payload: { status, substatus },
})

export const setAzureFormMetadata = (metadata: {
  formStatus?: AzureFormStatus
  engagementId?: string
  salesStage?: string
}) => ({
  type: AzureFormActions.SET_AZURE_FORM_METADATA,
  payload: metadata,
})

export const getAzureSolutions =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // Fetch solutions from API
      const solutions = await fetchAzureReferralSolutions(partnerId)
      dispatch(setAzureSolutions(solutions))
    } catch (e: unknown) {
      const error = e as AxiosError<APIReferralError>
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      throw error
    }
  }

type NormalizedAzureReferralResponse = {
  referralDetails: Record<string, unknown>
  referralId?: string
  partnerId?: string
  formStatus?: AzureFormStatus | string
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  salesStage?: string
}

export const normalizeAzureReferralApiResponse = (
  rawData: Record<string, unknown>
): NormalizedAzureReferralResponse => {
  const camelizedData = camelize(rawData) as Record<string, unknown>
  const nestedReferralDetails =
    (camelizedData.referralDetails as Record<string, unknown> | undefined) ||
    (camelizedData.referral_details as Record<string, unknown> | undefined)
  const referralDetails = nestedReferralDetails ?? camelizedData

  return {
    referralDetails,
    referralId:
      (camelizedData.referralId as string | undefined) ||
      (camelizedData.referral_id as string | undefined) ||
      (camelizedData.id as string | undefined),
    partnerId:
      (camelizedData.partnerId as string | undefined) ||
      (camelizedData.partner_id as string | undefined),
    formStatus:
      (camelizedData.formStatus as string | undefined) ||
      (camelizedData.form_status as string | undefined),
    status:
      (camelizedData.status as string | undefined) ||
      (referralDetails.status as string | undefined),
    substatus:
      (camelizedData.subStatus as string | undefined) ||
      (camelizedData.sub_status as string | undefined) ||
      (camelizedData.substatus as string | undefined) ||
      (referralDetails.substatus as string | undefined),
    salesStage: referralDetails.salesStage as string | undefined,
  }
}

/**
 * Drops partner team fields from CRM template prefills. Create-flow users add
 * team members manually; edit/view still hydrates them via getAzureReferral.
 */
export const omitTeamMembersFromCrmPrefill = (
  formData: Partial<ReferralAzureFormSections>
): Partial<ReferralAzureFormSections> => {
  if (!formData.dealDetails) {
    return formData
  }

  const dealDetails: Partial<DealDetails> = { ...formData.dealDetails }
  delete dealDetails.teamMembers
  delete dealDetails.contactDetails

  return {
    ...formData,
    dealDetails,
  }
}

/**
 * Prefills an Azure referral from Mapping Manager crm-field-values.
 * HubSpot templates return a flat Labra-keyed referral_object; GET/edit
 * payloads may already be MPC nested. Arrays on the MPC path are preserved
 * because teams and solutions are native arrays for mapAzureApiToForm.
 * Partner team members are intentionally not prefilled.
 */
export const fetchAndSetAzureCrmFieldValues =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    crmPrimaryKeyValue?: string,
    crmUserId?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_CRM_FIELD_VALUES))

    try {
      const response = await postCrmFieldValues(
        crmId,
        accountId,
        templateId,
        crmPrimaryKeyValue,
        crmUserId
      )
      const referralObject = response?.data?.referral_object

      if (isEmpty(referralObject)) {
        return { success: false }
      }

      const azureState = getState().azureReferralForm
      const existingReferralMetadata =
        azureState.referral?.referralMetadata || {}
      const existingReferralObject =
        (azureState.referral?.referralObject as Record<string, unknown>) || {}

      let mergedReferralObject: Record<string, unknown>
      let transformedFormData: Partial<ReferralAzureFormSections>

      if (isAzureLabraKeyedReferralObject(referralObject)) {
        mergedReferralObject = {
          ...existingReferralObject,
          ...referralObject,
        }
        transformedFormData = omitTeamMembersFromCrmPrefill(
          azureLabraObjectToForm(mergedReferralObject)
        )
      } else {
        const existingReferralDetails =
          (existingReferralObject.referral_details as
            | Record<string, unknown>
            | undefined) ||
          (existingReferralObject.referralDetails as
            | Record<string, unknown>
            | undefined) ||
          existingReferralObject
        const mappedReferralDetails =
          (referralObject.referral_details as
            | Record<string, unknown>
            | undefined) ||
          (referralObject.referralDetails as
            | Record<string, unknown>
            | undefined) ||
          referralObject
        mergedReferralObject = {
          ...existingReferralDetails,
          ...mappedReferralDetails,
        }
        transformedFormData = omitTeamMembersFromCrmPrefill(
          mapAzureApiToForm({
            referralDetails: mergedReferralObject,
          })
        )
      }

      await dispatch(setAzureCreationData(transformedFormData))
      await dispatch(
        setAzureDetails({
          referralMetadata: existingReferralMetadata,
          referralObject: mergedReferralObject,
          referralObjectTransformed: transformedFormData,
        })
      )

      return { success: true, data: mergedReferralObject }
    } catch (error: unknown) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return { success: false, error }
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_CRM_FIELD_VALUES))
    }
  }

export const getAzureReferral =
  (partnerId: string, referralId: string, crmType: string = 'hubspot') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const partnerData = getState().AccountData.user.partnerData
    const crmId = partnerData?.crmId || ''

    const actions = {
      setReferralDetails: setAzureDetails,
      setReferralCreationData: setAzureCreationData,
      transform: mapAzureApiToForm,
    }

    const { data } = await fetchReferralAzure(
      partnerId,
      referralId,
      crmType
    ).catch((e: unknown) => {
      const error = e as AxiosError<APIReferralError>
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      throw error
    })
    const referralData = data as Record<string, unknown>

    const normalized = normalizeAzureReferralApiResponse(referralData)
    const apiResponse = { referralDetails: normalized.referralDetails }

    const responseFormData = actions.transform(
      apiResponse
    ) as Partial<ReferralAzureFormSections>

    const referralIdFromResponse = normalized.referralId || referralId
    const referralStatus: ReferralMetadata['referralStatus'] =
      isAzureFormStatus(normalized.formStatus)
        ? normalized.formStatus
        : AZURE_FORM_STATUS.SUBMITTED

    // Set the form creation data (following the same pattern as getReferral)
    await dispatch(
      actions.setReferralCreationData(
        responseFormData as Partial<ReferralAzureFormSections>
      )
    )

    // Transform the form data back to API format for storage
    const apiPayloadForStorage = mapAzureFormToApi(responseFormData)
    const engagementId = normalized.referralDetails.engagementId as
      | string
      | undefined
    if (engagementId !== undefined) {
      apiPayloadForStorage.referralDetails.engagementId = engagementId
    }

    await dispatch(
      setAzureFormMetadata({
        formStatus: referralStatus,
        ...(engagementId !== undefined ? { engagementId } : {}),
        ...(normalized.salesStage !== undefined
          ? { salesStage: normalized.salesStage }
          : {}),
      })
    )

    await dispatch(
      actions.setReferralDetails({
        referralMetadata: {
          crmId: crmId,
          partnerId,
          referralType: 'referral_opportunity' as ReferralMetadataType,
          referralStatus,
          crmParentTable: 'labra_referral',
          crmPrimaryKeyName: 'id',
          ...(!isEmpty(referralIdFromResponse) && {
            referralId: referralIdFromResponse,
          }),
          ...(!isEmpty(referralIdFromResponse) && {
            crmPrimaryKeyValue: referralIdFromResponse,
          }),
        },
        referralObject: snakeize(apiPayloadForStorage) as Record<
          string,
          unknown
        >,
        referralObjectTransformed: responseFormData,
      })
    )

    const referralTeamMembers: AzureTeamMember[] = (
      (normalized.referralDetails.team as Array<Record<string, unknown>>) || []
    )
      .filter(member => member.email && member.firstName && member.lastName)
      .map(member => ({
        firstName: member.firstName as string,
        lastName: member.lastName as string,
        email: member.email as string,
        phoneNumber: member.phoneNumber as string | undefined,
      }))
    dispatch(setAzureTeamMembers(referralTeamMembers))

    if (normalized.status || normalized.substatus) {
      dispatch(
        setAzureStatus(normalized.status || '', normalized.substatus || '')
      )
    }
  }
